/**
 * English, and the REFERENCE KEY SET.
 *
 * Reference for completeness only — Czech is the fallback at runtime, here as
 * on the till. An English sentence in front of a Czech server is worse than a
 * missing string: it tells them the software is not for them.
 */

export const en = {
  locale: "en",
  messages: {
    // --- Shell -------------------------------------------------------------
    // --- Sign-in -----------------------------------------------------------

    // --- Role names ---------------------------------------------------------
    // The roster showed raw identifiers — "manager", "staff" — which is the same
    // database key on a screen that made the kitchen header read "KITCHEN".
    "role.owner": "Owner",
    "role.admin": "Administrator",
    "role.manager": "Manager",
    "role.shift_lead": "Shift lead",
    "role.staff": "Server",
    "role.viewer": "Viewer",
    "signIn.title": "Sign in",
    "signIn.subtitle": "Enter your PIN — the same one you use on the till.",
    "signIn.staff": "Who you are",
    "signIn.staffPlaceholder": "e.g. Eva S.",
    "signIn.staffHint": "Your name as the till shows it.",
    "signIn.pin": "PIN",
    "signIn.submit": "Sign in",
    "signIn.working": "Signing in…",
    "signIn.badCredentials": "That name or PIN is not right.",
    "signIn.notBackOffice": "This role does not have back-office access.",
    "signIn.unreachable": "The server is not responding. Check the connection.",
    "signIn.tooMany": "Too many attempts. Try again in {minutes} min.",
    "signIn.signedInAs": "Signed in as",
    "signIn.signOut": "Sign out",
    "signIn.expiresAt": "Session ends",
    // --- Command palette ---------------------------------------------------
    "palette.label": "Go to a screen",
    "palette.placeholder": "Search screens…",
    "palette.open": "Search",
    "palette.here": "you are here",
    "palette.nothing": "Nothing here by that name",
    "palette.hintMove": "move",
    "palette.hintOpen": "open",
    "palette.hintClose": "close",
    "shell.menu": "Menu",
    "shell.closeMenu": "Close menu",

    "app.title": "Back office",
    "nav.today": "Today",
    "nav.sales": "Sales",
    "nav.checks": "Receipts",
    "nav.shifts": "Shifts",
    "nav.kitchen": "Kitchen",
    "nav.fiscal": "Tax",
    "nav.tableCards": "Table cards",
    "tableCards.lede": "Print one card per table. A guest scans it, opens their own check and orders — nobody has to come over.",
    "tableCards.tablesLabel": "Tables",
    "tableCards.placeholder": "12\n13\nterrace 3",
    "tableCards.hint": "One table per line, named the way you name them. Cards:",
    "tableCards.duplicates": "Listed twice:",
    "tableCards.print": "Print cards",
    "tableCards.expires": "These cards stop working",
    "tableCards.callToAction": "Scan to order",
    "tableCards.modeWarning": "This venue opens its checks at the till. The cards will print, but until you switch the model in Settings a guest will be told to ask a server.",
    "tableCards.noBaseUrl": "Nothing says where the guest app lives, so a card carries the bare code. A camera will offer to copy it rather than open it.",
    "nav.menu": "Menu",
    "nav.staff": "People",
    "nav.settings": "Settings",
    "nav.group.running": "Running the place",
    "nav.group.money": "Money",
    "nav.group.setup": "Setup",
    "nav.group.soon": "In preparation",

    // --- Common ------------------------------------------------------------
    "common.loading": "Loading…",
    "common.failed": "Could not load this",
    "common.retry": "Try again",
    "common.empty": "Nothing here yet",
    "common.today": "Today",
    "common.week": "Last 7 days",
    "common.month": "Last 30 days",
    "common.custom": "Custom",
    "common.from": "From",
    "common.to": "To",
    "common.unreachable": "The server did not answer. Nothing here is a zero — it is unknown.",
    "common.of": "of",
    "common.total": "Total",
    "common.date": "Date",
    "common.time": "Time",
    "common.none": "None",

    // --- Today -------------------------------------------------------------
    "today.takings": "Takings today",
    "today.checks": "Receipts",
    "today.average": "Average receipt",
    "today.open": "Open tables",
    "today.openMoney": "On open tables",
    "today.vat": "VAT",
    "today.payments": "Payment mix",
    "today.quiet": "Nothing has been sold yet today.",
    "today.quietHint": "The trading day starts at 04:00.",

    // --- Sales -------------------------------------------------------------
    "sales.heading": "Sales",
    "sales.day": "Trading day",
    "sales.checkCount": "Receipts",
    "sales.revenue": "Revenue",
    "sales.breakdown": "Breakdown",
    "sales.tab.items": "Items",
    "sales.tab.staff": "Servers",
    "sales.tab.hours": "Hours",
    "sales.category": "Category",
    "sales.item": "Item",
    "sales.quantity": "Quantity",
    "sales.discounts": "Discounts",
    "sales.server": "Server",
    "sales.voids": "Voids",
    "sales.hour": "Hour",
    "sales.other": "Other",
    "sales.vat": "VAT",
    "sales.net": "Net",
    "sales.covers": "Guests",
    "sales.perCover": "Per guest",
    "sales.coversHint": "{n} guests at tables — counter sales are not counted",
    "sales.average": "Average",
    "sales.tips": "Tips",
    "sales.rounding": "Rounding",
    "sales.byRate": "By VAT rate",
    "sales.byTender": "By payment form",
    "sales.gaps": "Days with no sales",
    "sales.gapsWhy":
      "A missing trading day blocks the period from being closed — output VAT is fixed to the date of supply, so a day filed late needs an amended return.",
    "sales.tipsNotRevenue": "Tips are a liability to staff and are not revenue.",

    // --- Checks ------------------------------------------------------------
    "checks.heading": "Receipts",
    "checks.number": "Number",
    "checks.table": "Table",
    "checks.opened": "Opened",
    "checks.closed": "Closed",
    "checks.state": "State",
    "checks.stateOpen": "Open",
    "checks.openSince": "Open since {day}",
    "checks.stateSettled": "Settled",
    "checks.outstanding": "Outstanding",
    "checks.staff": "Served by",
    "checks.lines": "Items",
    "checks.voided": "Voided",
    "voidReason.RUNG_IN_ERROR": "Rung in error",
    "voidReason.GUEST_CHANGED_MIND": "Guest changed their mind",
    "voidReason.SENT_BACK": "Sent back from the kitchen",
    "voidReason.SPILLED": "Spilled or dropped",
    "voidReason.OTHER": "Other reason",
    "checks.tenders": "Payments",
    "checks.anomalies": "Things that did not add up",
    "checks.noNumber": "No receipt number",
    "checks.tab": "Tab {n}",
    "checks.fiscal.reported": "Reported to the tax authority",
    "checks.fiscal.test": "Acknowledged by the TEST service",
    "checks.fiscal.testWhy": "A test acknowledgement reports nothing. This sale is not filed.",
    "checks.fiscal.refused": "Refused",
    "checks.fiscal.pending": "Not yet reported",
    "checks.fiscal.none": "No submission was ever queued for this sale",

    // --- Shifts ------------------------------------------------------------
    "shifts.heading": "Shifts",
    "shifts.cashier": "Cashier",
    "shifts.expected": "Expected",
    "shifts.counted": "Counted",
    "shifts.variance": "Difference",
    "shifts.open": "Still open",
    "shifts.uncounted": "Not counted yet",
    "shifts.over": "Over",
    "shifts.short": "Short",
    "shifts.exact": "Exact",
    "shifts.ok": "Within tolerance",
    "shifts.watch": "Worth a look",
    "shifts.alert": "Needs investigating",
    "shifts.breakdown": "How the expectation was reached",
    "shifts.breakdownShort": "Breakdown",
    "shifts.openingFloatMinor": "Opening float",
    "shifts.cashSalesMinor": "Cash sales",
    "shifts.cashRefundsMinor": "Cash refunds",
    "shifts.roundingMinor": "Rounding",
    "shifts.payInsMinor": "Paid in",
    "shifts.payOutsMinor": "Paid out",
    "shifts.safeDropsMinor": "Dropped to safe",
    "shifts.cashTipsMinor": "Cash tips",
    "shifts.tipOutsMinor": "Tips paid out",
    "shifts.movements": "Cash movements",
    "shifts.overIsNotSafe":
      "A consistent overage is raised as loudly as a shortage: it is often cash taken and not yet removed.",
    "shifts.unattributed":
      "Cash rung with no drawer open. It is in no Z-report below and balances against nothing.",
    "shifts.ambiguous": "of which two drawers were open at once",

    // --- Kitchen -----------------------------------------------------------
    "kitchen.heading": "Kitchen",
    "kitchen.median": "Median time to the pass",
    "kitchen.p90": "Slowest tenth",
    "kitchen.tickets": "Tickets",
    "kitchen.served": "Bumped",
    "kitchen.station": "Station",
    "kitchen.late": "Late tickets",
    "kitchen.whyPercentile": "Percentiles, not an average — a guest experiences the tail.",

    // --- Fiscal ------------------------------------------------------------
    "fiscal.heading": "Tax authority",
    "fiscal.eet": "EET",
    "fiscal.notSetUp": "Not set up",
    "fiscal.notSetUpWhy": "Mandatory from 1 Jan 2027. The taxpayer's DIČ and the establishment number go in Settings.",
    "fiscal.credentials": "Signing certificate",
    "fiscal.present": "Installed",
    "fiscal.absent": "Missing",
    "fiscal.absentWhy": "Without the signing certificate no sale can be sent.",
    "fiscal.oldestPending": "Oldest sale still owed",
    "fiscal.quarantined": "Refused permanently",
    "fiscal.attempts": "attempts",
    "fiscal.andMore": "and this many more, not shown:",
    "fiscal.showRows": "Show more receipts",
    "fiscal.kind.NO_TILL_ID": "The till has no fiscal id",
    "fiscal.kind.NO_RECEIPT_NUMBER": "The check has no receipt number",
    "fiscal.kind.NUMBER_OUTSIDE_LEASE": "The receipt number is outside the leased range",
    "fiscal.kind.NUMBER_ALREADY_ASSIGNED": "The receipt number is already on another check",
    "fiscal.playground": "Acknowledged by the test service",
    "fiscal.playgroundWhy":
      "A test acknowledgement discharges nothing. These sales look reported and are not.",
    "fiscal.unreportable": "Cannot be reported",
    "fiscal.clean": "Nothing owed and nothing refused.",
    "fiscal.numbering": "Receipt numbering",
    "fiscal.gaps": "Gaps",
    "fiscal.gapsClean": "No gaps — every number leased has been spent or returned.",
    "fiscal.issued": "Issued",

    // --- Menu --------------------------------------------------------------
    "menu.heading": "Menu",
    "menu.tab.items": "Items",
    "menu.tab.categories": "Categories",
    "menu.tab.groups": "Sides & options",
    "menu.tab.stations": "Stations",
    "menu.search": "Search the menu",
    "menu.allCategories": "Everything",
    "menu.uncategorised": "Uncategorised",
    "menu.category": "Category",
    "menu.newItem": "New item",
    "menu.newCategory": "New category",
    "menu.newGroup": "New group",
    "menu.newStation": "New station",
    "menu.editItem": "Edit",
    "menu.off": "Off",
    "menu.switchOff": "Switch off",
    "menu.switchOn": "Switch on",
    "menu.empty": "Nothing here yet.",
    "menu.saved": "Saved. The tills get it when you publish.",
    "menu.saveFailed": "Could not save: {detail}",
    "menu.priceHint": "VAT included, as the guest sees it.",
    "menu.rateAfterPublish": "after publishing",
    "menu.itemCount": "{count} items",
    "menu.usedBy": "On {count} items",
    "menu.moveUp": "Up",
    "menu.moveDown": "Down",
    "menu.remove": "Remove",
    "menu.addOption": "Add option",
    "menu.required": "Required",
    "menu.optional": "Optional",
    "menu.noGroups": "Sides and options — \"Příloha\", \"Bez cibule\" — are offered on the till when an item is rung. A required one opens by itself.",
    "menu.taxCategory": "Tax category",
    "menu.tax.FOOD": "Food",
    "menu.tax.NON_ALC_BEVERAGE": "Soft drink",
    "menu.tax.ALCOHOL": "Alcohol",
    "menu.tax.TAP_WATER": "Tap water",
    "menu.tax.MILK_BEVERAGE": "Milk drink",
    "menu.tax.NON_FOOD": "Non-food",
    "menuImport.open": "Import from a spreadsheet",
    "menuImport.title": "Import a menu from a spreadsheet",
    "menuImport.lead": "A CSV file — from Excel via “Save as CSV”, or a Storyous export. The items go into the draft; the tills do not sell them until you publish the menu.",
    "menuImport.file": "File",
    "menuImport.choose": "Choose a file…",
    "menuImport.noFile": "No file chosen",
    "menuImport.unreadable": "The file could not be read as a table.",
    "menuImport.header": "The first row is headings",
    "menuImport.column.name": "Name",
    "menuImport.column.price": "Price incl. VAT",
    "menuImport.column.category": "Category",
    "menuImport.column.vat": "VAT rate",
    "menuImport.column.none": "—",
    "menuImport.columnN": "Column {n}",
    "menuImport.items": "Items",
    "menuImport.station": "Station",
    "menuImport.tax": "Tax",
    "menuImport.uncategorised": "No category",
    "menuImport.problems": "These rows will not be added",
    "menuImport.problem.name": "no name",
    "menuImport.problem.price": "the price cannot be read",
    "menuImport.taxHint": "The tax follows what the item is. The file's rate is only checked against the law: at a table beer and Kofola are both 21 %, but only beer is alcohol.",
    "menuImport.mismatches": "The file's rate differs from the law — the items are added; check them",
    "menuImport.mismatch": "{file} % in the file, {rate} % at a table",
    "menuImport.line": "line {line}",
    "menuImport.more": "and {count} more",
    "menuImport.add": "Add to the draft ({count})",
    "menuImport.adding": "Adding…",
    "menuImport.done": "Added to the draft: {created}.",
    "menuImport.skipped": "Already on the menu, skipped: {count}.",
    "menuImport.publishHint": "Check them and publish the menu — until then the tills do not sell them.",
    "menuImport.close": "Done",
    "setup.heading": "Before you start selling",
    "setup.progress": "{done} of {total} done",
    "setup.hide": "Hide",
    "setup.open": "Open",
    "setup.step.company": "The company on the receipt — name and IČO",
    "setup.step.menu": "The menu, published to the tills",
    "setup.step.tables": "Tables",
    "setup.step.people": "People and their PINs",
    "setup.step.publish": "The venue, published to the tills",
    "setup.step.devices": "Paired tills and kitchen screens",
    "setup.step.pohoda": "Pohoda cash desks (for the accountant)",
    "menuImport.failed": "The import failed: {detail}",
    "menu.field.name": "Name",
    "menu.field.price": "Price for the guest",
    "menu.field.description": "Description",
    "menu.field.ageRestricted": "18+ only",
    "menu.field.active": "On the menu",
    "menu.field.groups": "Sides & options",
    "menu.field.min": "Must choose at least",
    "menu.field.max": "Can choose at most",
    "menu.field.maxUnlimited": "no limit",
    "menu.field.options": "Options",
    "menu.field.optionName": "Option",
    "menu.field.priceDelta": "Surcharge",
    "menu.field.maxQuantity": "At most",
    "menu.problem.name": "The group needs a name.",
    "menu.problem.optionName": "Every option needs a name.",
    "menu.problem.price": "A surcharge must be an amount, like 25 or 25,50.",
    "menu.problem.tooFew": "It requires more choices than it offers — nothing carrying it could be rung.",
    "menu.problem.max": "The maximum must be a whole number, at least the minimum.",
    "allergen.GLUTEN": "Gluten",
    "allergen.CRUSTACEANS": "Crustaceans",
    "allergen.EGGS": "Eggs",
    "allergen.FISH": "Fish",
    "allergen.PEANUTS": "Peanuts",
    "allergen.SOYBEANS": "Soybeans",
    "allergen.MILK": "Milk",
    "allergen.NUTS": "Nuts",
    "allergen.CELERY": "Celery",
    "allergen.MUSTARD": "Mustard",
    "allergen.SESAME": "Sesame",
    "allergen.SULPHITES": "Sulphites",
    "allergen.LUPIN": "Lupin",
    "allergen.MOLLUSCS": "Molluscs",
    "menu.edit": "Change price",
    "menu.editing": "The price a guest sees, VAT included.",
    "menu.save": "Save",
    "menu.cancel": "Back",
    "menu.available": "On sale",
    "menu.soldOut": "Sold out",
  "menu.availability": "Availability",
  "menu.availabilityFailed": "Could not load what is currently sold out. The Availability column may be out of date.",
  "menu.markSoldOut": "Mark sold out",
  "menu.soldOutReason": "Reason",
  "menu.soldOutDefault": "Sold out",
  "menu.soldOutConfirm": "Take off the menu",
  "menu.backOn": "Put back on",
    "menu.draft": "Changes are not live until you publish.",
    "menu.publish": "Publish a new version",
    "menu.publishing": "Publishing…",
    "menu.published": "Published as version {version}. Tills pick it up within a minute.",
    "menu.editFailed": "That change could not be saved.",
    "menu.version": "Version",
    "menu.item": "Item",
    "menu.station": "Goes to",
    "menu.dineIn": "Eat in",
    "menu.takeaway": "Takeaway",
    "menu.rate": "VAT",
    "menu.allergens": "Allergens",
    "menu.whyTwoPrices":
      "The same dish is 12 % eaten in and 21 % taken away. The rate is resolved per line, never stored on the product.",

    // --- Staff & settings --------------------------------------------------
    "staff.heading": "People",
    "staff.name": "Name",
    "staff.role": "Role",
    "staff.add": "Add a person",
    "staff.nameHint": "As the till will show it — “Jana N.”",
    "staff.pin": "PIN",
    "staff.newPin": "New PIN",
    "staff.pinHint": "Six to twelve digits. Tell them — it is never shown again.",
    "staff.pinResetHint": "Leave empty to keep the PIN.",
    "staff.active": "Works here",
    "staff.added": "Added. They appear on the tills at the next sync.",
    "staff.saved": "Saved.",
    "staff.savedTills": "Saved. The tills forget the old PIN and role at their next sync.",
    "staff.left": "No longer here",
    "staff.count": "{n} on the team",
    "venue.version": "On the tills: version",
    "venue.unpublished": "Changes not on the tills yet",
    "venue.publish": "Publish to the tills",
    "venue.saved": "Saved. It reaches the tills when published.",
    "venue.published": "Published — the tills fetch it now.",
    "venue.company": "Company on the receipt",
    "venue.guests": "Guests",
    "venue.guestOrdering": "Guests may order from their phones",
    "venue.guestOrderingLead": "The QR code on a check always shows the bill. Ordering from it works only when this is on — it applies at once, no publishing needed.",
    "venue.companyLead": "As the tax document has to show it.",
    "venue.legalName": "Company name",
    "venue.registrationId": "Company no. (IČO)",
    "venue.vatId": "VAT no. (VAT payers only)",
    "venue.address": "Registered address",
    "venue.eic": "EET: taxpayer id",
    "venue.unit": "EET: establishment no.",
    "venue.tables": "Tables",
    "venue.addTable": "Add a table",
    "venue.noArea": "No area",
    "venue.seats": "seats",
    "venue.seatsLabel": "Number of seats",
    "venue.newTable": "New table",
    "venue.editTable": "Edit table",
    "venue.ownerOnly": "Only the owner can change this.",
    "venue.seats.one": "seat",
    "venue.seats.few": "seats",
    "venue.seats.many": "seats",
    "venue.seats.other": "seats",
    "venue.tableName": "Name",
    "venue.area": "Area",
    "venue.tableActive": "In use",
    "devices.heading": "Devices",
    "devices.pair": "Pair a device",
    "devices.codeFor": "Code for",
    "devices.codeHow": "On the new device, open the app and type it in.",
    "devices.codeUntil": "Good once, until",
    "devices.done": "Done",
    "devices.name": "Name",
    "devices.namePlaceholder": "e.g. Bar tablet",
    "devices.kind": "Kind",
    "devices.till": "Till",
    "devices.kitchen": "Kitchen screen",
    "devices.station": "Station",
    "devices.makeCode": "Make a code",
    "devices.unpair": "Unpair",
    "devices.place": "Where it stands",
    "devices.atTables": "At the tables",
    "devices.atCounter": "At the counter — pay first, an order number",
    "devices.pickupScreen": "Pickup — the numbers screen for guests",
    "devices.counterChip": "Counter {prefix}",
    "devices.toCounter": "To the counter",
    "devices.toTables": "To the tables",
    "venue.noTables": "No tables yet. Add the first one — the tills show them after you publish.",
    "devices.none": "No devices paired yet. Pair the first till with a code.",

    // --- The accountant's file (Pohoda) -----------------------------------
    "tender.CASH": "Cash",
    "tender.CARD": "Card",
    "tender.MEAL_VOUCHER": "Meal vouchers",
    "tender.GIFT_CARD": "Gift card",
    "tender.HOUSE_ACCOUNT": "House account",
    "tender.QR_TRANSFER": "QR payment",
    "tender.VOUCHER": "Voucher",
    "tender.ROOM_CHARGE": "Room charge",
    "tender.EXTERNAL": "Other payment",
    "pohoda.heading": "For the accountant",
    "pohoda.lead": "The takings as a file for Pohoda: one receipt voucher (příjmový pokladní doklad) per day and payment form, split by VAT rate.",
    "pohoda.month": "Month",
    "pohoda.toYesterday": "to yesterday",
    "pohoda.download": "Download for Pohoda",
    "pohoda.downloaded": "Downloaded. Import the file in Pohoda through XML import.",
    "pohoda.noCashAccount": "No Pohoda cash desk is set for:",
    "pohoda.fixInSettings": "Set it in Settings.",
    "pohoda.noRegistrationId": "The company has no IČO. Add it in Settings.",
    "pohoda.nothing": "Nothing was sold in this period.",
    "pohoda.dayNotClosed": "Today is not over yet.",
    "pohoda.notSetUp": "The Pohoda settings are not valid. Check them in Settings.",
    "pohoda.training": "This is the training venue: nothing here is booked, so there is nothing to export for Pohoda.",
    "pohoda.settings": "Pohoda",
    "pohoda.settingsLead": "Where each payment form is booked in Pohoda. Copy the cash desk and posting-rule abbreviations exactly as they are in Pohoda — ideally from your accountant.",
    "pohoda.cashAccount": "Cash desk (Pokladna)",
    "pohoda.accounting": "Posting rule (předkontace)",
    "pohoda.optional": "optional",
    "pohoda.saved": "Saved.",
    "pohoda.tooLong": "At most 19 characters.",
    "devices.confirmUnpair": "Unpair “{name}”? It stops working at its next contact with the server.",
    "settings.heading": "Settings",
    "settings.venue": "Venue",
    "settings.timezone": "Time zone",
    "settings.dayStart": "Trading day starts",
    "settings.fiscalId": "Tax registration",
    "settings.devices": "Tills",
    "settings.language": "Language",

    // --- Preview -----------------------------------------------------------
    "preview.badge": "In preparation",
    "preview.note": "This is a sketch of a screen that is not built yet. Nothing here is real data.",
    "preview.stock": "Stock",
    "preview.purchasing": "Supplier invoices",
    "preview.rota": "Rotas",
    "preview.analytics": "Analytics",
    "preview.loyalty": "Loyalty",
    // --- Modules ----------------------------------------------------------
    "modules.title": "Modules",
    "modules.lead": "What this venue uses. Selling, receipts, the drawer and EET are always on; the rest you switch on within your plan.",
    "modules.plan": "Plan: {plan}",
    "modules.noPlan": "No plan — selling and receipts only.",
    "modules.planLapsed": "The plan has lapsed and optional modules are off. Selling carries on.",
    "modules.preset": "Kind of venue",
    "modules.custom": "Custom",
    "modules.save": "Save modules",
    "modules.saved": "Saved. Tills and screens pick it up within seconds.",
    "modules.notInPlan": "Not in your plan",
    "modules.needs": "Needs: {modules}",
    "modules.always": "Always on",
    "modules.soon": "Coming",
    "modules.noServiceMode": "Keep tables, a counter or both — otherwise nothing can be sold.",
    "preset.cafe": "Café",
    "preset.restaurant": "Restaurant",
    "preset.cafe-restaurant": "Café + restaurant",
    "preset.pub": "Pub / bar",
    "preset.bakery": "Bakery / takeaway",
    "preset.food-truck": "Food truck",
    "module.sales.name": "Selling and receipts",
    "module.sales.lead": "The till, payments, receipts, shifts, EET, the menu, people and takings.",
    "module.tables.name": "Tables",
    "module.tables.lead": "The floor plan, checks at the table, moving and splitting.",
    "module.counter.name": "Counter",
    "module.counter.lead": "Selling at the counter: pay first, an order number, here or to go.",
    "module.kitchen-display.name": "Kitchen display",
    "module.kitchen-display.lead": "Tickets on a screen in the kitchen and at the bar, allergens, bumping.",
    "module.kitchen-printing.name": "Kitchen printing",
    "module.kitchen-printing.lead": "Tickets on the printer in the kitchen and at the bar.",
    "module.courses.name": "Courses",
    "module.courses.lead": "Starters now, mains when called.",
    "module.guest-ordering.name": "Ordering from the table",
    "module.guest-ordering.lead": "Guests order from their phones through the code on the table.",
    "module.public-menu.name": "Menu on the web",
    "module.public-menu.lead": "A public menu with photos and allergens, in several languages.",
    "module.online-ordering.name": "Online pickup orders",
    "module.online-ordering.lead": "Order and pay ahead, pick up at the counter at the time chosen.",
    "module.reservations.name": "Reservations",
    "module.reservations.lead": "Table bookings from the web and by phone, on the floor plan.",
    "module.stock.name": "Stock",
    "module.stock.lead": "Ingredients, recipes, depletion by sale, deliveries, counts, food cost.",
    "module.accounting.name": "Accounting",
    "module.accounting.lead": "Takings as a file for Pohoda.",
    "module.loyalty.name": "Loyalty",
    "module.loyalty.lead": "Points and rewards for regulars.",
    "module.crm.name": "Guests",
    "module.crm.lead": "Who comes, and how often.",
    "module.foodsafety.name": "Food safety",
    "module.foodsafety.lead": "HACCP records and inspections.",

    // --- Stock (the Sklad screens) ---------------------------------------------
    "nav.reservations": "Bookings",
    "res.title": "Bookings",
    "res.lead": "Who is coming today, how full the room is when, and bookings by phone.",
    "res.prev": "Previous day",
    "res.next": "Next day",
    "res.today": "Today",
    "res.covers": "Guests",
    "res.bookings": "Bookings",
    "res.capacity": "Seats at once",
    "res.strip": "How full through the evening",
    "res.none": "Nobody has booked for this day.",
    "res.time": "Time",
    "res.name": "Name",
    "res.party": "Party",
    "res.phone": "Phone",
    "res.table": "Table",
    "res.note": "Note",
    "res.source": "From",
    "res.status": "Status",
    "res.source.ONLINE": "web",
    "res.source.PHONE": "phone",
    "res.source.WALK_IN": "walk-in",
    "res.status.BOOKED": "coming",
    "res.status.SEATED": "seated",
    "res.status.NO_SHOW": "no-show",
    "res.status.CANCELLED": "cancelled",
    "res.seat": "Seat",
    "res.noShow": "No-show",
    "res.cancel": "Cancel",
    "res.restore": "Restore",
    "res.noTable": "— no table —",
    "res.add": "New booking",
    "res.addHint": "By phone, or for a guest who called in.",
    "res.save": "Book",
    "res.full": "That time is full. Book anyway?",
    "res.force": "Book anyway",
    "res.closed": "You are closed then.",
    "res.saved": "Booked.",
    "nav.history": "Change history",
    "nav.printers": "Printers",
    "print.lead": "What each printer was given — and what did not print, and why.",
    "print.none": "No printers yet. The kitchen has its screens, and receipts show on the till.",
    "print.empty": "Nothing in the last 24 hours.",
    "print.unrouted": "No printer",
    "print.transport.SCREEN": "On screen",
    "print.transport.HUB": "A printer on the venue's network",
    "print.transport.CLOUDPRNT": "A Star printer via CloudPRNT",
    "print.columns": "{n} characters a line",
    "print.receipts": "the till's receipts",
    "print.status.PRINTED": "Printed {at}",
    "print.status.QUEUED": "Waiting for the printer",
    "print.status.STALE": "Not printed: sent at {fired}, reached the printer only at {at}",
    "print.status.UNROUTED": "Not printed: no printer prints for {station}",
    "print.copy": "Copy",
    "print.reprint": "Print again",
    "print.reprinted": "A copy went to the printer.",
    "print.setup": "Set up the printer",
    "print.form.name": "Name",
    "print.form.transport": "Connection",
    "print.form.columns": "Roll width",
    "print.form.stations": "Prints tickets for",
    "print.form.receipts": "Prints the till's receipts",
    "print.form.active": "Switched on",
    "print.form.save": "Save the printer",
    "print.saved": "Saved — the tills know.",
    "print.forbidden": "Shift leads and above see the printers.",
    "audit.printer.saved": "Printer {subject} set up",
    "audit.print.reprinted": "A copy on {subject}",
    "audit.n.lines.one": "{n} line",
    "audit.n.lines.few": "{n} lines",
    "audit.n.lines.many": "{n} lines",
    "audit.n.lines.other": "{n} lines",
    "audit.n.seats.one": "{n} seat",
    "audit.n.seats.few": "{n} seats",
    "audit.n.seats.many": "{n} seats",
    "audit.n.seats.other": "{n} seats",
    "audit.n.changes.one": "{n} change",
    "audit.n.changes.few": "{n} changes",
    "audit.n.changes.many": "{n} changes",
    "audit.n.changes.other": "{n} changes",
    "audit.title": "Change history",
    "audit.lead": "Who changed what in the office — prices, people, devices, stock. The record cannot be altered without it showing.",
    "audit.intact": "The record is intact — verified: {n}.",
    "audit.broken": "The record has been altered: change no. {n} does not match its fingerprint. Contact us.",
    "audit.none": "Nothing was changed in this period.",
    "audit.when": "When",
    "audit.who": "Who",
    "audit.what": "What",
    "audit.system": "System",
    "audit.forbidden": "The change history is for the manager and the owner.",
    "audit.menu.item.created": "New dish {subject} at {price}",
    "audit.menu.item.changed": "{subject} edited",
    "audit.menu.item.priced": "Price of {subject}: {before} → {after}",
    "audit.menu.published": "Menu sent to the tills (version {version})",
    "audit.staff.added": "New team member: {subject}",
    "audit.staff.changed": "{subject} changed",
    "audit.staff.left": "{subject} has left",
    "audit.staff.pin.reset": "New PIN for {subject}",
    "audit.device.pairing.issued": "A pairing code for a new device",
    "audit.device.unpaired": "A device unpaired",
    "audit.device.service.changed": "A device switched: {mode}",
    "audit.mode.COUNTER": "counter {prefix}",
    "audit.mode.TABLE": "to the tables",
    "audit.venue.identity.changed": "Company details edited",
    "audit.venue.table.saved": "Table {subject}: {seats}",
    "audit.venue.published": "Settings sent to the tills (version {version})",
    "audit.venue.modules.changed": "Modules changed",
    "audit.venue.public.changed": "Public page edited",
    "audit.stock.received": "Delivery: {lines} for {price}",
    "audit.stock.wasted": "Write-off: {lines}",
    "audit.stock.counted": "Stocktake: {lines}",
    "audit.stock.item.saved": "Ingredient {subject} edited",
    "audit.stock.supplier.saved": "Supplier {subject}",
    "audit.stock.recipe.versioned": "Recipe for {subject}, version {version}",
    "audit.stock.option-delta.versioned": "What a guest's choice changes, version {version}",
    "audit.booking.made": "Booking for {party}",
    "audit.booking.forced": "Booking for {party} past capacity",
    "audit.booking.changed": "Booking: {status}",
    "nav.stock": "Stock",
    "stock.tab.overview": "Overview",
    "stock.tab.items": "Ingredients",
    "stock.tab.recipes": "Recipes",
    "stock.tab.receive": "Deliveries",
    "stock.tab.count": "Stocktake",
    "stock.tab.waste": "Waste",
    "stock.tab.suppliers": "Suppliers",
    "stock.value": "Stock value",
    "stock.valueHint": "At average purchase prices before VAT.",
    "stock.foodCost": "Food cost",
    "stock.foodCostHint": "What the ingredients of what sold cost, as a share of its takings before VAT — dishes with a recipe only.",
    "stock.period.today": "Today",
    "stock.period.week": "7 days",
    "stock.period.month": "30 days",
    "stock.running": "Running low",
    "stock.runningNone": "Nothing is running low.",
    "stock.runningLine": "{left} left, reorder below {min}",
    "stock.byDish": "By dish",
    "stock.dish": "Dish",
    "stock.sold": "Sold",
    "stock.takings": "Takings before VAT",
    "stock.cost": "Ingredients cost",
    "stock.share": "Share",
    "stock.noSales": "Nothing with a recipe sold in this period.",
    "stock.unmapped": "Sold without a recipe",
    "stock.unmappedHint": "Nothing is taken out of stock for these. Give them a recipe — it applies from the moment it is saved.",
    "stock.timesSold": "sold {n}×",
    "stock.writeRecipe": "Write a recipe",
    "stock.problems": "What the ledger could not record",
    "stock.addItem": "Add an ingredient",
    "stock.item.name": "Name",
    "stock.item.category": "Group",
    "stock.item.categoryHint": "Bar, Kitchen, Cellar… — the stocktake lists them the same way.",
    "stock.item.onHand": "On hand",
    "stock.item.unitCost": "Unit cost",
    "stock.item.value": "Value",
    "stock.item.unit": "Kept in",
    "stock.item.unitHint": "It cannot change after the first movement.",
    "stock.item.reorder": "Reorder when it falls below",
    "stock.item.standardCost": "Cost until the first delivery",
    "stock.item.standardCostHint": "CZK before VAT per {unit}. After that, the average of deliveries counts.",
    "stock.item.active": "In use",
    "stock.item.low": "low",
    "stock.unit.g": "kilograms (kg)",
    "stock.unit.ml": "litres (l)",
    "stock.unit.ea": "pieces (pcs)",
    "stock.noItems": "No ingredients yet. Add the first — coffee or milk, say.",
    "stock.perUnit": "per {unit}",
    "stock.saved": "Saved.",
    "stock.recipe.lead": "What one portion uses. A new version applies from when it is saved; what sold before keeps the old one.",
    "stock.recipe.portionCost": "A portion costs",
    "stock.recipe.price": "Price before VAT",
    "stock.recipe.none": "no recipe",
    "stock.recipe.uncosted": "No price yet: {items}",
    "stock.recipe.ingredient": "Ingredient",
    "stock.recipe.quantity": "Quantity",
    "stock.recipe.save": "Save a new version",
    "stock.recipe.version": "version {n}",
    "stock.recipe.saved": "Saved. It applies to everything sold from now on.",
    "stock.options": "Guests' choices",
    "stock.options.lead": "What a choice changes in stock, per one taken: oat milk is +0.2 l oat and −0.2 l milk.",
    "stock.options.none": "changes nothing in stock",
    "stock.options.signedHint": "A positive quantity adds, a negative one takes away.",
    "stock.receive.supplier": "Supplier",
    "stock.receive.noSupplier": "— no supplier —",
    "stock.receive.document": "Delivery note or invoice number",
    "stock.receive.netTotal": "Total before VAT (CZK)",
    "stock.receive.submit": "Take into stock",
    "stock.receive.done": "Taken in. Average costs are recalculated.",
    "stock.recent": "Recent movements",
    "stock.line.add": "Another line",
    "stock.line.remove": "Remove the line",
    "stock.line.pick": "— pick an ingredient —",
    "stock.line.incomplete": "Every line needs an ingredient and a quantity.",
    "stock.pending": "Recorded; the stock updates within seconds.",
    "stock.count.last": "The last count · {when}",
    "stock.count.lastExact": "Everything matched.",
    "stock.count.lead": "Count what is on the shelf, whatever the system says. You see the differences before anything is recorded.",
    "stock.count.counted": "Counted",
    "stock.count.expected": "Expected",
    "stock.count.variance": "Difference",
    "stock.count.check": "Check the differences",
    "stock.count.post": "Record the count",
    "stock.count.back": "Edit",
    "stock.count.done": "Count recorded. Stock now matches what was counted.",
    "stock.count.total": "Total difference",
    "stock.count.empty": "Enter at least one quantity.",
    "stock.waste.reason": "Reason",
    "stock.waste.submit": "Write off",
    "stock.waste.done": "Written off.",
    "stock.reason.SPOILED": "Spoiled",
    "stock.reason.DAMAGED": "Damaged",
    "stock.reason.EXPIRED": "Past its date",
    "stock.reason.PREP_ERROR": "Kitchen mistake",
    "stock.reason.RETURNED_BY_GUEST": "Sent back by a guest",
    "stock.reason.STAFF_MEAL": "Staff meal",
    "stock.reason.VOID_AFTER_FIRE": "Voided after it was fired",
    "stock.reason.COUNT": "Stocktake",
    "stock.reason.LATE_BEFORE_COUNT": "Sale from before the count",
    "stock.kind.RECEIPT": "Delivery",
    "stock.kind.SALE_DEPLETION": "Sale",
    "stock.kind.WASTE": "Waste",
    "stock.kind.STAFF_MEAL": "Staff meal",
    "stock.kind.COUNT_ADJUSTMENT": "Stocktake",
    "stock.supplier.add": "Add a supplier",
    "stock.supplier.registration": "Company number",
    "stock.supplier.none": "No suppliers yet.",
    "today.lowStock": "Running low: {items}",
    "stock.options.edit": "Edit",
    "stock.unmappedMore": "and {n} more",
    "approvals.title": "Approved without the authority",
    "approvals.lead": "The till should have refused these. The sale stands; somebody should look into it.",
    "approvals.act.VOID_SENT": "Voided food the kitchen already had",
    "approvals.act.COMP": "Comp",
    "approvals.act.DISCOUNT": "Discount",
    "approvals.act.REFUND": "Refund",
    "approvals.problem.NOT_PERMITTED": "{name} may not approve this",
    "approvals.problem.UNKNOWN_APPROVER": "approved by somebody unknown ({name})",
    "approvals.problem.INACTIVE_APPROVER": "{name} no longer works here",
    "approvals.open": "Open the check",
    "checks.pickup": "Pickup {code}",
    "tender.ONLINE": "Online payment",
    "public.title": "Public page and online orders",
    "public.lead": "Where guests find you: the menu, your hours, and orders to collect.",
    "public.slug": "Page address",
    "public.slugHint": "Lower-case letters, digits and hyphens — \"lipa-vinohrady\", say.",
    "public.notYet": "The page has no address yet.",
    "public.open": "Open the page",
    "public.tagline": "Tagline",
    "public.address": "Address",
    "public.phone": "Phone",
    "public.hours": "Opening hours",
    "public.hoursHint": "A closing time before the opening time means after midnight.",
    "public.closed": "closed",
    "public.addRange": "+ another time",
    "public.day.mon": "Monday",
    "public.day.tue": "Tuesday",
    "public.day.wed": "Wednesday",
    "public.day.thu": "Thursday",
    "public.day.fri": "Friday",
    "public.day.sat": "Saturday",
    "public.day.sun": "Sunday",
    "public.online": "Taking online orders",
    "public.onlineHint": "Switching it off pauses orders at once — when you are full, say.",
    "public.prep": "Preparation (minutes)",
    "public.slot": "Pickup times every (minutes)",
    "public.bookings": "Taking bookings online",
    "public.bookingsHint": "Guests book a table on your page. Staff can always book by phone.",
    "public.capacity": "Guests seated at once",
    "public.duration": "Table held for (minutes)",
    "public.bookingSlot": "Booking times every (minutes)",
    "public.maxParty": "Largest party online",
    "public.daysAhead": "Days ahead",
    "public.notice": "Notice needed (minutes)",
    "public.saved": "Saved. The page changes at once.",
    "menu.guests": "For guests online",
    "menu.photo": "Photo",
    "menu.photoUpload": "Upload a photo",
    "menu.photoRemove": "Remove the photo",
    "menu.photoHint": "We shrink it in the browser — only a small copy is sent.",
    "menu.photoFailed": "The photo could not be uploaded.",
    "menu.nameEn": "Name in English",
    "menu.descriptionEn": "Description in English",
    "menu.dietary": "Suits",
    "menu.diet.VEGAN": "vegans",
    "menu.diet.VEGETARIAN": "vegetarians",
    "menu.diet.GLUTEN_FREE": "gluten-free",
    "menu.diet.LACTOSE_FREE": "lactose-free",
    "menu.diet.SPICY": "spicy",
    "menu.featured": "Featured — at the top of the page",
  },
} as const;

/**
 * The KEY SET from English, with values that are merely strings.
 *
 * `as const` above makes every value a literal type, which is what gives the
 * key union its precision — and would make every other catalogue fail to
 * compile, since "Tržby" is not the literal type "Sales". Taking the keys and
 * widening the values is the whole trick: a missing key is a type error in
 * cs/sk/uk, a surplus key is a type error, and a translation is just a string.
 */
export type MessageKey = keyof typeof en.messages;
export type Messages = Record<MessageKey, string>;
